// mission-state-reducer.ts
//
// Reducer THUẦN: (state, delta, seq) → state mới. Không I/O, không Date.now(),
// không random — nhờ vậy "rebuild từ journal" luôn cho ra đúng state như "áp dụng
// dần" (property test 15). Mọi thứ không thuần (ghi file, thời gian) nằm ở
// mission-store.ts.
//
// State là PROJECTION (cache dựng lại được), journal mới là sự thật. Vì thế reducer
// được phép đơn giản: v1 chỉ giữ đủ thứ P5/P6/P7 cần đọc.
//
// `attempt_finished`'s category/reasons[] and `attempt_recovered`'s evidence
// (Ticket*Record below) go through SecretRedactor before being stored —
// defense in depth: reducer is the LAST point before journal.jsonl
// (append-only, forever), never trusts callers' own redaction alone.
import type {
  ManualResolutionRecord,
  MissionDelta,
  MissionState,
  OperationIntentRecord,
} from "./mission-state-types.js";
import { SecretRedactor } from "./secret-redactor.js";

function redactOnce(text: string): string {
  const redactor = new SecretRedactor({ secretValues: [] });
  return redactor.feed(text) + redactor.flush();
}

// Re-exported so existing call sites (and tests) that import these types
// from mission-state-reducer.ts keep working after the split (phase 10, lô A).
export type {
  MissionDelta,
  MissionDeltaKind,
  MissionState,
  OperationIntentRecord,
  OperationResultRecord,
  TicketRecoveryRecord,
  TicketReportedGaps,
  TicketVerdictRecord,
} from "./mission-state-types.js";

export function emptyMissionState(): MissionState {
  return {
    tickets: {},
    attempts: { started: 0, finished: 0 },
    intents: {},
    results: {},
    review_receipts: [],
    recovered_count: 0,
    ticket_last_verdict: {},
    orphan_recovered_count: 0,
    ticket_last_recovery: {},
    ticket_reported_gaps: {},
    last_event_seq: 0,
  };
}

function redactedStrings(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value.filter((v): v is string => typeof v === "string").map(redactOnce);
}

function str(value: unknown): string | undefined {
  return typeof value === "string" ? value : undefined;
}

/** `{ manual_resolution }` when the delta carries a well-formed one, else `{}` (the key stays absent). */
function manualResolutionOf(value: unknown): { manual_resolution?: ManualResolutionRecord } {
  const raw = (value ?? {}) as Record<string, unknown>;
  const actor = str(raw.actor);
  const answer = str(raw.answer);
  const at = str(raw.at);
  return actor && answer && at ? { manual_resolution: { actor, answer, at } } : {};
}

/** Áp dụng một delta. KHÔNG mutate `state` — luôn trả object mới. */
export function reduceMission(state: MissionState, delta: MissionDelta, seq: number): MissionState {
  const next: MissionState = {
    ...state,
    tickets: { ...state.tickets },
    attempts: { ...state.attempts },
    intents: { ...state.intents },
    results: { ...state.results },
    review_receipts: [...state.review_receipts],
    ticket_last_verdict: { ...state.ticket_last_verdict },
    ticket_last_recovery: { ...state.ticket_last_recovery },
    ticket_reported_gaps: { ...state.ticket_reported_gaps },
    last_event_seq: seq,
  };

  switch (delta.kind) {
    case "mission_started": // [B1] ABORTED sticky (supervisor-halt-directive-step.ts header).
      next.mission_id = str(delta.mission_id) ?? next.mission_id;
      next.status = next.status === "ABORTED" ? next.status : (str(delta.status) ?? "RUNNING");
      break;
    case "mission_status_changed": // [B1] ABORTED sticky, same as above.
      next.status = next.status === "ABORTED" ? next.status : (str(delta.status) ?? next.status);
      break;
    case "mission_workspace_ready":
      next.mission_id = str(delta.mission_id) ?? next.mission_id;
      break;
    case "ticket_state_changed": {
      const ticket = str(delta.ticket);
      if (ticket) next.tickets[ticket] = str(delta.status) ?? "unknown";
      break;
    }
    case "attempt_started":
      next.attempts.started += 1;
      break;
    case "attempt_finished": {
      next.attempts.finished += 1;
      const ticket = str(delta.ticket);
      const category = str(delta.category);
      const reasons = Array.isArray(delta.reasons)
        ? delta.reasons.filter((r): r is string => typeof r === "string")
        : undefined;
      const schemaErrorDetail = str(delta.schema_error_detail);
      const rawResultTail = str(delta.raw_result_tail);
      if (ticket && category && reasons) {
        next.ticket_last_verdict[ticket] = {
          category,
          reasons: reasons.map(redactOnce),
          ...(schemaErrorDetail !== undefined
            ? { schema_error_detail: redactOnce(schemaErrorDetail) }
            : {}),
          ...(rawResultTail !== undefined ? { raw_result_tail: redactOnce(rawResultTail) } : {}),
        };
      }
      // A2: last declaration wins, no merge across attempts — an array the
      // delta omits reads as "declared none this time", never as the
      // previous attempt's list. Untouched when neither field is present.
      const uncoveredAreas = redactedStrings(delta.uncovered_areas);
      const remainingWork = redactedStrings(delta.remaining_work);
      if (ticket && (uncoveredAreas !== undefined || remainingWork !== undefined)) {
        next.ticket_reported_gaps[ticket] = {
          uncovered_areas: uncoveredAreas ?? [],
          remaining_work: remainingWork ?? [],
        };
      }
      break;
    }
    case "operation_intent": {
      const id = str(delta.operation_id);
      if (id)
        next.intents[id] = {
          operation_id: id,
          operation_kind: str(delta.operation_kind) ?? "unknown",
          params: (delta.params as Record<string, unknown>) ?? {},
          seq,
        };
      break;
    }
    case "operation_result": {
      const id = str(delta.operation_id);
      if (id)
        next.results[id] = {
          operation_id: id,
          outcome: str(delta.outcome) ?? "unknown",
          remote_id: str(delta.remote_id),
          ...manualResolutionOf(delta.manual_resolution),
          seq,
        };
      break;
    }
    case "review_receipt_written": {
      const key = str(delta.receipt_path) ?? str(delta.candidate_sha);
      if (key && !next.review_receipts.includes(key)) next.review_receipts.push(key);
      break;
    }
    case "journal_recovered":
      next.recovered_count += 1;
      break;
    case "attempt_recovered": {
      next.orphan_recovered_count += 1;
      const ticket = str(delta.ticket);
      const reason = str(delta.reason);
      const evidence = str(delta.evidence);
      if (ticket && reason && evidence)
        next.ticket_last_recovery[ticket] = { reason, evidence: redactOnce(evidence) };
      break;
    }
    case "candidate_review_finished": {
      const ticket = str(delta.ticket);
      const sha = str(delta.candidate_sha);
      if (ticket && sha)
        next.candidate_review_sha = { ...state.candidate_review_sha, [ticket]: sha };
      break;
    }
    default:
      break;
  }
  return next;
}

/** Intent đã ghi mà CHƯA có result — reconciler phải quan sát thực tại trước khi retry. */
export function listOpenIntents(state: MissionState): OperationIntentRecord[] {
  return Object.values(state.intents)
    .filter((intent) => state.results[intent.operation_id] === undefined)
    .sort((a, b) => a.seq - b.seq);
}
